import { useEffect } from 'react'
import { OpenAPI } from '@/api/generated/core/OpenAPI'
import { useAppSelector } from '@/store/hooks'

/**
 * 서버가 준 로고 URL 을 <img src> 에 쓸 수 있는 주소로 바꾼다.
 * `/api/...` 상대 경로는 API 서버 주소(VITE_API_BASE_URL)를 붙인다. 비어 있으면 같은 오리진.
 */
export const resolveOrgLogoUrl = (url: string | null | undefined): string | null => {
  if (!url) return null
  return url.startsWith('/') ? `${OpenAPI.BASE}${url}` : url
}

/**
 * 현재 선택된 워크스페이스 기관의 로고 주소. 올린 로고가 없으면 null — 기본 DOT 로고를 쓴다.
 * 로고는 기관 관리에서 올리고 지운다.
 */
export const useOrgLogoUrl = (): string | null => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  return resolveOrgLogoUrl(selectedWorkspace?.org_logo_url)
}

/** 기본 탭 아이콘·제목 (index.html 과 같은 값) */
const DEFAULT_FAVICON = '/favicon.png'
const DEFAULT_SITE_TITLE = '성과관리솔루션'

/**
 * 선택된 워크스페이스 기관의 타이틀 로고(브라우저 탭 아이콘)와 탭 제목을 적용한다. 없으면 기본값.
 * 타이틀 로고는 기관 관리에서 올리고 지운다.
 */
export const useOrgFavicon = (): void => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const faviconUrl = resolveOrgLogoUrl(selectedWorkspace?.org_favicon_url) ?? DEFAULT_FAVICON

  useEffect(() => {
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
      .forEach(link => {
        link.href = faviconUrl
        // 기본 아이콘은 PNG. 올린 이미지는 형식이 다양해 type 을 지워 브라우저가 판단하게 한다
        if (faviconUrl === DEFAULT_FAVICON) link.type = 'image/png'
        else link.removeAttribute('type')
      })
  }, [faviconUrl])

  // 탭 제목: 기관이 정한 제목, 없으면 기본 제목
  const siteTitle = selectedWorkspace?.org_site_title?.trim() || DEFAULT_SITE_TITLE
  useEffect(() => {
    document.title = siteTitle
  }, [siteTitle])
}
